import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '@/config/firebase.config';
import { Rating } from '@/types';
import { COLLECTIONS } from '@/constants';
import { convertFirestoreDoc } from '@/utils';
import { updateUserRating } from './user.service';
import { updateExchange } from './exchange.service';

/**
 * Create a rating for an exchange
 */
export const createRating = async (
  exchangeId: string,
  fromUserId: string,
  toUserId: string,
  rating: number,
  comment?: string
): Promise<string> => {
  try {
    // Validate rating
    if (rating < 1 || rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }

    const ratingData = {
      exchangeId,
      fromUserId,
      toUserId,
      rating,
      comment: comment || '',
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(collection(db, COLLECTIONS.RATINGS), ratingData);

    // Update user's average rating
    await updateUserRating(toUserId, rating);

    // Mark exchange as rated
    await updateExchange(exchangeId, { isRated: true });

    return docRef.id;
  } catch (error) {
    console.error('Error creating rating:', error);
    throw error;
  }
};

/**
 * Get ratings for a user
 */
export const getUserRatings = async (userId: string): Promise<Rating[]> => {
  try {
    const ratingsRef = collection(db, COLLECTIONS.RATINGS);
    const q = query(ratingsRef, where('toUserId', '==', userId));

    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) =>
      convertFirestoreDoc<Rating>({
        id: doc.id,
        ...doc.data(),
      })
    );
  } catch (error) {
    console.error('Error getting user ratings:', error);
    throw error;
  }
};

/**
 * Get rating for a specific exchange
 */
export const getExchangeRating = async (exchangeId: string): Promise<Rating | null> => {
  try {
    const ratingsRef = collection(db, COLLECTIONS.RATINGS);
    const q = query(ratingsRef, where('exchangeId', '==', exchangeId));

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const doc = querySnapshot.docs[0];
    if (!doc) return null;

    return convertFirestoreDoc<Rating>({
      id: doc.id,
      ...doc.data(),
    });
  } catch (error) {
    console.error('Error getting exchange rating:', error);
    throw error;
  }
};

/**
 * Check if user has rated an exchange
 */
export const hasRatedExchange = async (
  exchangeId: string,
  userId: string
): Promise<boolean> => {
  try {
    const ratingsRef = collection(db, COLLECTIONS.RATINGS);
    const q = query(
      ratingsRef,
      where('exchangeId', '==', exchangeId),
      where('fromUserId', '==', userId)
    );

    const querySnapshot = await getDocs(q);
    return !querySnapshot.empty;
  } catch (error) {
    console.error('Error checking if user rated exchange:', error);
    return false;
  }
};
